import {
  RENT_MATRIX_PAGE_SIZE,
  RENT_TRACKING_INVALIDATION_DOMAIN,
  type RentFilter,
  type RentGroupBy,
  type RentMatrixPage,
  type RentRowKind,
  type RentSort,
  type RentValueMode,
  type ScopeInput,
} from '@rentguru/commons-utils';
import { queryOptions, type QueryClient, type QueryFunctionContext } from '@tanstack/react-query';
import isNil from 'lodash/isNil';

import {
  fetchRentMatrix,
  fetchRentMatrixFilterOptions,
  fetchRentMatrixPagesWhile,
  fetchRentMatrixRows,
} from './rentMatrixApi';
import { FIRST_PAGE_INDEX } from './rentMatrixWindowUtils';
import { canReuseAsPlaceholder, type RentPlaceholderView } from './rentTrackingViewUtils';

const RENT_TRACKING_QUERY_ROOT = RENT_TRACKING_INVALIDATION_DOMAIN;
const RENT_WIDGET_ROW_LIMIT = 30;
const RENT_TRACKING_STALE_TIME_IN_MS = 15 * 60 * 1000;
const RENT_MATRIX_EXTRA_PAGE_GC_TIME_IN_MS = 30 * 1000;
const RENT_MATRIX_TOTALS_LIMIT = 0;
const TRANSIENT_QUERY_TIME_IN_MS = 0;
const NO_QUERY_HASHES: ReadonlySet<string> = new Set();
const QUERY_KEY_PARAMETERS_INDEX = 4;

export enum RentTrackingQueryKind {
  FILTER_OPTIONS = 'filterOptions',
  PAGE = 'page',
  ROWS = 'rows',
  TOTALS = 'totals',
  UNPAID_DRILL = 'unpaidDrill',
  WIDGET = 'widget',
}

const REFETCHED_QUERY_KINDS: RentTrackingQueryKind[] = [
  RentTrackingQueryKind.PAGE,
  RentTrackingQueryKind.ROWS,
  RentTrackingQueryKind.TOTALS,
  RentTrackingQueryKind.UNPAID_DRILL,
  RentTrackingQueryKind.WIDGET,
];

export interface RentMatrixView {
  filter: RentFilter | null;
  fiscalYear: number;
  groupBy: RentGroupBy | null;
  mode: RentValueMode;
  rowView: RentRowKind;
  sort: RentSort;
}

type RentMatrixTotalsView = Omit<RentMatrixView, 'sort'>;

export interface RentTrackingScope {
  scope: ScopeInput;
  scopeHash: string;
}

type RentQueryContext = Pick<QueryFunctionContext, 'signal'>;

type RentQueryKey<Parameters> = readonly [string, string | null, RentTrackingQueryKind, string, Parameters];

const toTotalsView = ({ filter, fiscalYear, groupBy, mode, rowView }: RentMatrixView): RentMatrixTotalsView => ({
  filter,
  fiscalYear,
  groupBy,
  mode,
  rowView,
});

export const rentTrackingKeys = {
  all: (clientId: string | null) => [RENT_TRACKING_QUERY_ROOT, clientId] as const,
  kind: (clientId: string | null, kind: RentTrackingQueryKind) => [RENT_TRACKING_QUERY_ROOT, clientId, kind] as const,
  filterOptions: (clientId: string | null, scopeHash: string, fiscalYear: number, rowView: RentRowKind) =>
    [
      RENT_TRACKING_QUERY_ROOT,
      clientId,
      RentTrackingQueryKind.FILTER_OPTIONS,
      scopeHash,
      { fiscalYear, rowView },
    ] as const,
  page: (clientId: string | null, scopeHash: string, view: RentMatrixView, pageIndex: number) =>
    [RENT_TRACKING_QUERY_ROOT, clientId, RentTrackingQueryKind.PAGE, scopeHash, { ...view, pageIndex }] as const,
  rows: (clientId: string | null, scopeHash: string, view: RentMatrixView, leaseIds: string[]) =>
    [
      RENT_TRACKING_QUERY_ROOT,
      clientId,
      RentTrackingQueryKind.ROWS,
      scopeHash,
      { ...toTotalsView(view), leaseIds },
    ] as const,
  totals: (clientId: string | null, scopeHash: string, view: RentMatrixView) =>
    [RENT_TRACKING_QUERY_ROOT, clientId, RentTrackingQueryKind.TOTALS, scopeHash, toTotalsView(view)] as const,
  unpaidDrill: (clientId: string | null, scopeHash: string, view: RentMatrixView) =>
    [RENT_TRACKING_QUERY_ROOT, clientId, RentTrackingQueryKind.UNPAID_DRILL, scopeHash, view] as const,
  widget: (clientId: string | null, scopeHash: string, view: RentMatrixView, limit: number) =>
    [RENT_TRACKING_QUERY_ROOT, clientId, RentTrackingQueryKind.WIDGET, scopeHash, { ...view, limit }] as const,
};

const keepCompatiblePlaceholder =
  (next: RentPlaceholderView) =>
  (
    previousData: RentMatrixPage | undefined,
    previousQuery?: { queryKey: RentQueryKey<RentPlaceholderView> }
  ): RentMatrixPage | undefined =>
    canReuseAsPlaceholder(previousQuery?.queryKey[QUERY_KEY_PARAMETERS_INDEX], next) ? previousData : undefined;

export const rentWidgetOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  view: RentMatrixView
) =>
  queryOptions({
    enabled: !isNil(clientId),
    placeholderData: keepCompatiblePlaceholder(view),
    queryFn: ({ signal }: RentQueryContext) =>
      fetchRentMatrix({ ...view, page: { limit: RENT_WIDGET_ROW_LIMIT }, scope }, signal),
    queryKey: rentTrackingKeys.widget(clientId, scopeHash, view, RENT_WIDGET_ROW_LIMIT),
    refetchOnReconnect: 'always',
    staleTime: RENT_TRACKING_STALE_TIME_IN_MS,
  });

export const rentUnpaidDrillOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  view: RentMatrixView,
  hasMoreDebtors: (page: RentMatrixPage) => boolean
) =>
  queryOptions({
    enabled: !isNil(clientId),
    queryFn: ({ signal }: RentQueryContext) => fetchRentMatrixPagesWhile({ ...view, scope }, hasMoreDebtors, signal),
    queryKey: rentTrackingKeys.unpaidDrill(clientId, scopeHash, view),
    refetchOnReconnect: 'always',
    staleTime: RENT_TRACKING_STALE_TIME_IN_MS,
  });

export const rentPageOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  view: RentMatrixView,
  pageIndex: number
) =>
  queryOptions({
    enabled: !isNil(clientId),
    gcTime: pageIndex === FIRST_PAGE_INDEX ? RENT_TRACKING_STALE_TIME_IN_MS : RENT_MATRIX_EXTRA_PAGE_GC_TIME_IN_MS,
    placeholderData: keepCompatiblePlaceholder({ ...view, pageIndex }),
    queryFn: ({ signal }: RentQueryContext) =>
      fetchRentMatrix(
        { ...view, page: { limit: RENT_MATRIX_PAGE_SIZE, offset: pageIndex * RENT_MATRIX_PAGE_SIZE }, scope },
        signal
      ),
    queryKey: rentTrackingKeys.page(clientId, scopeHash, view, pageIndex),
    refetchOnReconnect: 'always',
    staleTime: RENT_TRACKING_STALE_TIME_IN_MS,
  });

export const rentTotalsOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  view: RentMatrixView
) =>
  queryOptions({
    enabled: !isNil(clientId),
    placeholderData: keepCompatiblePlaceholder(view),
    queryFn: ({ signal }: RentQueryContext) =>
      fetchRentMatrix({ ...toTotalsView(view), page: { limit: RENT_MATRIX_TOTALS_LIMIT }, scope }, signal),
    queryKey: rentTrackingKeys.totals(clientId, scopeHash, view),
    refetchOnReconnect: 'always',
    staleTime: RENT_TRACKING_STALE_TIME_IN_MS,
  });

export const rentRowsOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  view: RentMatrixView,
  leaseIds: string[]
) =>
  queryOptions({
    gcTime: TRANSIENT_QUERY_TIME_IN_MS,
    queryFn: ({ signal }: RentQueryContext) =>
      fetchRentMatrixRows(
        { filter: view.filter, fiscalYear: view.fiscalYear, leaseIds, mode: view.mode, scope },
        signal
      ),
    queryKey: rentTrackingKeys.rows(clientId, scopeHash, view, leaseIds),
    staleTime: TRANSIENT_QUERY_TIME_IN_MS,
  });

export const rentFilterOptionsOptions = (
  clientId: string | null,
  { scope, scopeHash }: RentTrackingScope,
  fiscalYear: number,
  rowView: RentRowKind
) =>
  queryOptions({
    enabled: !isNil(clientId),
    queryFn: ({ signal }: RentQueryContext) => fetchRentMatrixFilterOptions({ fiscalYear, rowView, scope }, signal),
    queryKey: rentTrackingKeys.filterOptions(clientId, scopeHash, fiscalYear, rowView),
    staleTime: RENT_TRACKING_STALE_TIME_IN_MS,
  });

interface RentTrackingInvalidationOptions {
  exceptHashes?: ReadonlySet<string>;
}

export const invalidateRentTracking = async (
  queryClient: QueryClient,
  clientId: string | null,
  { exceptHashes = NO_QUERY_HASHES }: RentTrackingInvalidationOptions = {}
): Promise<void> => {
  await Promise.all([
    ...REFETCHED_QUERY_KINDS.map((kind) =>
      queryClient.invalidateQueries({
        predicate: (query) => !exceptHashes.has(query.queryHash),
        queryKey: rentTrackingKeys.kind(clientId, kind),
      })
    ),
    queryClient.invalidateQueries({
      queryKey: rentTrackingKeys.kind(clientId, RentTrackingQueryKind.FILTER_OPTIONS),
      refetchType: 'none',
    }),
  ]);
};
